// Modal that can be triggered from anywhere to show markets
import {
  ChangeEvent,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../assets/svg/close.svg";
import { ReactComponent as SearchIcon } from "../../assets/svg/search.svg";
import { InstrumentTypeResponse } from "../../codegen-api";
import { ICON_COLORS, LAYER_COLORS } from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { COMPONENTS, SPACING } from "../../constants/design/spacing";
import { MarketContext } from "../../contexts/MarketContext";
import { PageEndpointEnum } from "../../enums/endpoint";
import useScreenSize from "../../hooks/screenSize/useScreenSize";
import useWatchlist from "../../hooks/useWatchlist";
import { AssetResponse } from "../../utils/asset";
import { BaseModal } from "../BaseModal";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import { ClearButton } from "../MarketSelectionButton/style";
import { Input } from "../shared/Input";
import { Select } from "../shared/Select";
import CommandFooter from "./CommandFooter";
import {
  CommandContent,
  CommandWrapper,
  HeaderContainer,
  InputContainer,
  MarketFilterContainer,
  ModalContentWrapper,
  Section,
  Title,
} from "./style";
import { MarketFiltersEnum } from "./CommandModal.types";
import { marketFilterOptions } from "./CommandModal.helpers";
import { useSectionOrdering } from "./useSectionOrdering";
import { useMarketsData } from "./useMarketsData";
import {
  useCmdKToggle,
  useCommandKeyboardNav,
  useReloadStaleData,
  useResetOnClose,
} from "./CommandModal.effects";
import CommandSection from "./CommandSection";

function CommandModal() {
  const { t } = useTranslation("app", { keyPrefix: "CommandModal" });
  // Vertical names live in one shared i18n block so every surface that lists
  // trading types (picker sections, portfolio filter, market header) agrees.
  const { t: tTradingType } = useTranslation("app", {
    keyPrefix: "tradingTypes",
  });
  const navigate = useNavigate();
  const { isMobileScreen } = useScreenSize();
  const { market, setMarket, showCommandModal, setShowCommandModal } =
    useContext(MarketContext);
  const { watchlist, updateWatchlist } = useWatchlist();

  const {
    collapsedSections,
    toggleSectionCollapsed,
    sectionOrder,
    renderedSectionNamesRef,
    sectionSort,
  } = useSectionOrdering();

  const {
    marketFilter,
    setPreferredMarketFilter,
    mutateMarketSummary,
    mutateIndexHistories,
    marketsSummaryLastUpdatedMillis,
    setMarketsSummaryLastUpdatedMillis,
    searchText,
    setSearchText,
    highlightedMarketIndex,
    setHighlightedMarketIndex,
    sectionsData,
  } = useMarketsData(market, watchlist, t, tTradingType);

  // Toggle the menu when ⌘K is pressed
  useCmdKToggle(setShowCommandModal);

  const setMarketFilter = useCallback(
    (filter: MarketFiltersEnum) => {
      setPreferredMarketFilter(filter);
    },
    [setPreferredMarketFilter]
  );

  const onSearchChange = useCallback((evt: ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setSearchText(value);
  }, []);

  const onRowClick = useCallback(
    (asset: AssetResponse, derivative: InstrumentTypeResponse) => {
      setShowCommandModal(false);
      setMarket({
        asset,
        derivative,
      });
      navigate(PageEndpointEnum.TRADING);
    },
    [navigate, setMarket, setShowCommandModal]
  );

  // Handle navigation while the modal is open
  useCommandKeyboardNav(
    showCommandModal,
    highlightedMarketIndex,
    setHighlightedMarketIndex,
    sectionsData,
    onRowClick
  );

  // Reset modal states when closed
  useResetOnClose(showCommandModal, setSearchText, setHighlightedMarketIndex);

  // Reload data when modal is opened, and data is stale
  // This prevents spamming open/close modal from hitting the apis repeatedly
  useReloadStaleData(
    showCommandModal,
    marketsSummaryLastUpdatedMillis,
    setMarketsSummaryLastUpdatedMillis,
    mutateMarketSummary,
    mutateIndexHistories
  );

  const onWatchlistClick = useCallback(
    (asset: AssetResponse, derivative: InstrumentTypeResponse) => {
      const exist = watchlist.some(
        (w) => w.asset === asset && w.derivative === derivative
      );
      if (exist) {
        const newWatchlist = [...watchlist].filter(
          (w) => w.asset !== asset || w.derivative !== derivative
        );
        updateWatchlist(newWatchlist);
      } else {
        updateWatchlist([...watchlist, { asset, derivative }]);
      }
    },
    [updateWatchlist, watchlist]
  );

  const modalContent = useMemo(
    () => (
      <ModalContentWrapper isMobile={isMobileScreen}>
        <HeaderContainer>
          <InputContainer>
            <Input
              autoFocus={!isMobileScreen}
              type="text"
              placeholder={t("search_markets")}
              wrapperStyles={{
                backgroundColor: LAYER_COLORS.two,
                padding: `0 ${SPACING.three}px`,
              }}
              inputStyles={{
                fontSize: FONT_SIZE.two,
              }}
              leftAccessory={<SearchIcon stroke={ICON_COLORS.three} />}
              rightAccessory={
                searchText ? (
                  <ClearButton onClick={() => setSearchText("")}>
                    <CloseIcon />
                  </ClearButton>
                ) : undefined
              }
              value={searchText}
              onChange={onSearchChange}
            />
            <Button
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={() => setShowCommandModal(false)}
            >
              <CloseIcon />
            </Button>
          </InputContainer>
          <MarketFilterContainer>
            <Select
              isRound
              buttonSpacing={SPACING.three}
              options={marketFilterOptions.map((f) => {
                return {
                  label: t(f),
                  isActive: marketFilter === f,
                  onClick: () => setMarketFilter(f),
                };
              })}
            />
          </MarketFilterContainer>
        </HeaderContainer>
        <CommandWrapper>
          <CommandContent
            style={{
              paddingBottom: isMobileScreen
                ? SPACING.two + COMPONENTS.footer.mobileNav
                : COMPONENTS.cmdKModalFooter,
            }}
          >
            {(() => {
              // Sort visible sections by the user-defined order.
              const visibleNames = sectionsData.map((s) => s.name);
              const ordered = [
                ...sectionOrder.filter((n) => visibleNames.includes(n)),
                ...visibleNames.filter((n) => !sectionOrder.includes(n)),
              ];
              const orderedSections = ordered
                .map((name) => sectionsData.find((s) => s.name === name))
                .filter((s): s is typeof sectionsData[number] => !!s);
              // Capture current names for the pointer-sort hook callback.
              renderedSectionNamesRef.current = orderedSections.map(
                (s) => s.name
              );
              return orderedSections;
            })().map((s, sIndex) => (
              <CommandSection
                key={s.name}
                s={s}
                sIndex={sIndex}
                collapsedSections={collapsedSections}
                sectionSort={sectionSort}
                toggleSectionCollapsed={toggleSectionCollapsed}
                isMobileScreen={isMobileScreen}
                highlightedMarketIndex={highlightedMarketIndex}
                onRowClick={onRowClick}
                onWatchlistClick={onWatchlistClick}
                marketFilter={marketFilter}
                watchlist={watchlist}
                updateWatchlist={updateWatchlist}
              />
            ))}
            {/* NO RESULTS */}
            {!sectionsData.length && (
              <Section>
                <Title noResults>{t("no_search_results")}</Title>
              </Section>
            )}
          </CommandContent>
        </CommandWrapper>

        {!isMobileScreen && <CommandFooter />}
      </ModalContentWrapper>
    ),
    [
      highlightedMarketIndex,
      isMobileScreen,
      marketFilter,
      onRowClick,
      onSearchChange,
      onWatchlistClick,
      searchText,
      sectionsData,
      setMarketFilter,
      t,
      setShowCommandModal,
      watchlist,
      updateWatchlist,
      collapsedSections,
      toggleSectionCollapsed,
      sectionOrder,
      sectionSort,
    ]
  );

  const style = isMobileScreen
    ? {
        width: `calc(100% - ${SPACING.three * 2}px)`,
      }
    : {
        width: 640,
      };

  return (
    <BaseModal
      hideCloseButton
      noContentPadding
      show={showCommandModal}
      onHide={() => setShowCommandModal(false)}
      style={style}
    >
      {modalContent}
    </BaseModal>
  );
}

export default CommandModal;
